import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '@/features/manpower/manpower-query-keys';

/**
 * One Initiative's Field Force workload, for the Detail_Panel's Workload tab
 * (`complan-manpower` R5.1-R5.9, R5.8's calculation-chain disclosure).
 *
 * ## Why the shared demand endpoint rather than a new one
 *
 * `POST /api/manpower/demand` already accepts an `initiativeIds` filter and
 * returns one row per Field_Force_Demand_Driver with its calculated
 * `demandHours`, its period and Platform allocations, and the Effort_Assumption
 * version the figure was calculated against. Passing a single id is exactly the
 * per-Initiative case, so the panel reads the same engine the Demand Explorer and
 * Capacity & Utilisation screens read, and a number shown here cannot disagree
 * with the same number shown there.
 *
 * The Demand Explorer's own hook is deliberately not reused: it does not send
 * `initiativeIds` and its local wire mirror drops `platformAllocations`, so
 * widening it would change that screen's cache key and payload for no benefit
 * here. This is a sibling reader of one endpoint, not a fork of the engine.
 *
 * ## Confidentiality and capability
 *
 * The endpoint requires `manpower:read` and reads only Initiatives the principal
 * may read (R7.6), so a caller without the capability must not issue it at all -
 * hence the `enabled` flag rather than a caught 403. The panel renders a notice
 * instead.
 *
 * ## `demandHours: null` means Data_Incomplete
 *
 * R5.9: a driver whose Activity_Type / FF role / population-unit combination
 * resolves to no Effort_Assumption has no hours, and the wire says `null` rather
 * than `0`. Rendering it as zero would understate demand and hide a governance
 * finding, so the tab marks it.
 */

/** Granularity the panel reads; months match the period allocation the dummy shows. */
const PANEL_GRANULARITY = 'month' as const;

/** One period's share of a driver's Demand_Hours (R5.6). */
export interface WorkloadPeriodAllocation {
  readonly periodId: string;
  readonly hours: number;
}

/** One Platform's share of a driver's Demand_Hours (R5.5, the split that avoids double counting). */
export interface WorkloadPlatformAllocation {
  readonly platformId: string;
  readonly hours: number;
}

/** One calculated Field_Force_Demand_Driver of this Initiative. */
export interface WorkloadDriver {
  /** The `initiative_ff_scope` row id — joins back to `detail.fieldForceScope[].id`. */
  readonly driverId: string;
  readonly ffRole: string;
  readonly activityTypeId: string;
  readonly populationUnitId: string;
  readonly regionId: string | null;
  readonly effortAssumptionId: string | null;
  readonly effortAssumptionVersion: number | null;
  readonly calculationDate: string;
  /** `null` for a Data_Incomplete driver (R5.9). */
  readonly demandHours: number | null;
  readonly platformAllocations: readonly WorkloadPlatformAllocation[];
  readonly periodAllocations: readonly WorkloadPeriodAllocation[];
}

/** An Effort_Assumption combination the engine could not resolve (R5.9). */
export interface MissingEffortAssumption {
  readonly activityTypeId: string;
  readonly ffRole: string;
  readonly populationUnitId: string;
}

export interface InitiativeWorkload {
  readonly drivers: readonly WorkloadDriver[];
  /** This Initiative's rolled-up Demand_Hours. */
  readonly totalDemandHours: number;
  readonly driverCount: number;
  readonly dataIncompleteDriverCount: number;
  /** False when the engine's Reconciliation_Control did not hold (R5.7). */
  readonly reconciliationHolds: boolean;
  readonly missingEffortAssumptions: readonly MissingEffortAssumption[];
  /** The period grid the allocations are keyed by, in order. */
  readonly periods: readonly { readonly periodId: string; readonly start: string; readonly end: string }[];
}

const wireSchema = z.object({
  drivers: z
    .array(
      z.object({
        driverId: z.string(),
        initiativeId: z.string(),
        ffRole: z.string(),
        activityTypeId: z.string(),
        populationUnitId: z.string(),
        regionId: z.string().nullable().default(null),
        effortAssumptionId: z.string().nullable().default(null),
        effortAssumptionVersion: z.number().int().nullable().default(null),
        calculationDate: z.string(),
        demandHours: z.number().nullable().default(null),
        platformAllocations: z
          .array(z.object({ platformId: z.string(), hours: z.number() }))
          .default([]),
        periodAllocations: z
          .array(z.object({ periodId: z.string(), hours: z.number() }))
          .default([]),
      }),
    )
    .default([]),
  initiativeTotals: z
    .array(
      z.object({
        initiativeId: z.string(),
        totalDemandHours: z.number(),
        driverCount: z.number().int().min(0),
        dataIncompleteDriverCount: z.number().int().min(0),
      }),
    )
    .default([]),
  totalDemandHours: z.number().default(0),
  missingEffortAssumptions: z
    .array(
      z.object({
        activityTypeId: z.string(),
        ffRole: z.string(),
        populationUnitId: z.string(),
      }),
    )
    .default([]),
  reconciliation: z.object({ holds: z.boolean() }).partial().default({}),
  grid: z
    .object({
      granularity: z.string(),
      buckets: z
        .array(z.object({ periodId: z.string(), start: z.string(), end: z.string() }))
        .default([]),
    })
    .partial()
    .default({}),
});

/** Fetches one Initiative's workload from the shared demand engine. */
export async function fetchInitiativeWorkload(
  initiativeId: string,
  planningYear: number,
): Promise<InitiativeWorkload> {
  const body = await apiFetch<unknown>('/api/manpower/demand', {
    method: 'POST',
    body: JSON.stringify({
      planningYear,
      granularity: PANEL_GRANULARITY,
      initiativeIds: [initiativeId],
    }),
  });

  const parsed = wireSchema.parse(body);
  // The endpoint answers for a filtered *scope*; with one id in the filter there
  // is at most one total, and its absence means this Initiative contributed no
  // driver rows at all (no Field Force scope, or none in this planning year).
  const total = parsed.initiativeTotals.find((entry) => entry.initiativeId === initiativeId);

  return {
    drivers: parsed.drivers.filter((driver) => driver.initiativeId === initiativeId),
    totalDemandHours: total?.totalDemandHours ?? 0,
    driverCount: total?.driverCount ?? 0,
    dataIncompleteDriverCount: total?.dataIncompleteDriverCount ?? 0,
    reconciliationHolds: parsed.reconciliation.holds ?? true,
    missingEffortAssumptions: parsed.missingEffortAssumptions,
    periods: parsed.grid.buckets ?? [],
  };
}

/**
 * React Query hook for one Initiative's workload.
 *
 * Keyed under `manpowerQueryKeys` so an Effort_Assumption or Capacity_Period
 * write — which invalidates `manpowerQueryKeys.all` (task 14.4) — also refreshes
 * this panel, rather than leaving it showing hours calculated against a
 * superseded assumption version.
 */
export function useInitiativeWorkload(
  initiativeId: string,
  planningYear: number,
  options?: { readonly enabled?: boolean },
): UseQueryResult<InitiativeWorkload, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.initiativeWorkload({ initiativeId, planningYear }),
    queryFn: () => fetchInitiativeWorkload(initiativeId, planningYear),
    enabled: (options?.enabled ?? true) && initiativeId !== '',
  });
}
